<script setup lang="ts">
import LeLongArrow from "@dsk/icons/LeLongArrow.vue";
import { computed } from "vue";

import { config } from "@/config";
import type { Dictionary, DictionaryMethod } from "@/types";

const props = defineProps<{
  method: DictionaryMethod;
  dictionary: Dictionary;
}>();

const { language } = config.app;

const sourceLanguageCode = computed<string | undefined>(() => props.method.sourceLanguageCode);
const targetLanguageCode = computed<string | undefined>(() => props.method.targetLanguageCode);

const fromText = computed<string>(() => {
  if (language === "en") {
    return " from ";
  } else if (language === "de") {
    return " von ";
  }

  return " fra ";
});

const dictionary = computed<string>(() => {
  if (!props.dictionary.title || !props.dictionary.publisher) {
    return "";
  }

  return `${props.dictionary.title} ${fromText.value} ${props.dictionary.publisher}`;
});
</script>

<template>
  <div class="le-compact-footer">
    <span v-if="sourceLanguageCode && targetLanguageCode" class="le-compact-footer__direction">
      {{ sourceLanguageCode }} <LeLongArrow class="le-compact-footer__arrow" /> {{ targetLanguageCode }}
    </span>
    <span v-if="sourceLanguageCode && targetLanguageCode && dictionary">•</span>
    <span v-if="dictionary" class="le-compact-footer__description">{{ dictionary }}</span>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-compact-footer {
  display: flex;
  position: relative;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  overflow: hidden;
  border-radius: 0 0 4px 4px;
  background: @colors[@primary][100];
  color: @colors[@neutrals][300];
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    bottom: 0;
    width: 50px;
    background: linear-gradient(to right, transparent, @colors[@primary][100] 100%);
  }

  &__direction {
    flex-shrink: 0;
    text-transform: uppercase;
    .body-text("xs", "semi-bold", "italic");
  }

  &__arrow {
    width: 8px;
    height: 8px;
    margin: 0 4px;
  }

  &__arrow,
  &__description {
    flex-shrink: 0;
    .body-text("xs", @style: "italic");
  }
}
</style>
